import store from "../store/store";
import App from "../components/app";
import {i18nService} from "../services/i18n-service";

export class Product {
  public id: string;
  public brand: string;
  public country: string;
  public maintenance_group: string;
  public variants: Variant[];
  public web_category: string;
  public customer_group: string;
  public descriptions: Description[];

  constructor(values: any) {
    // this.id = values.id;
    this.brand = values.brand;
    this.country = values.country;
    this.maintenance_group = values.maintenance_group;
    let variants: Variant[] = [];
    if (values.variants) {
      for (let valueVariant of values.variants) {
        if (valueVariant) {
          variants.push(new Variant(valueVariant))
        }
      }
    }
    this.variants = variants;

    let descriptions: Description[] = [];
    if (values.descriptions) {

      for (let description of values.descriptions) {
        descriptions.push(new Description(description))
      }
    }
    this.descriptions = descriptions;

    this.web_category = values.web_category;
    this.customer_group = values.customer_group;
  }

  getVariant(productId: string, variantId: string): Variant {
    return this.variants.find(variant => {
      return variant.productId === productId && variant.id === variantId;
    });
  }

  getMatchingVariant(): Variant {
    const state = store.getState();

    // early exit if there is only one variant
    if (this.variants.length === 1) {
      return this.variants[0];
    }

    let remainingVariants: Variant[] = this.variants;
    //check coming-soon-filter
    const comingSoonFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == 'coming_soon');
    if (comingSoonFilter) {
      const comingSoonVariants = remainingVariants.filter(variant => variant.comingSoon);
      if (comingSoonVariants.length > 0) {
        remainingVariants = comingSoonVariants
      }
    }

    //check sale-filter
    const saleFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == "sale");
    if (saleFilter) {
      const saleVariants = remainingVariants.filter(variant => variant.sale);
      if (saleVariants.length > 0) {
        remainingVariants = saleVariants
      }
    }

    //check new-in-filter
    const newInFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == "new_in");
    if (newInFilter) {
      const newInVariants = remainingVariants.filter(variant => variant.newIn);
      if (newInVariants.length > 0) {
        remainingVariants = newInVariants
      }
    }

    //check highlight filter
    const highlightFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == 'highlight');
    if (highlightFilter) {
      const highlightVariants = remainingVariants.filter(variant => variant.highlight);
      if (highlightVariants.length > 0) {
        remainingVariants = highlightVariants
      }
    }

    //check color-filter
    const colorFilters = state.filters.selected.filter(it => it.type == FilterType.Color);
    if (colorFilters && colorFilters.length > 0) {
      const colorVariants = remainingVariants.filter(variant => {
        return colorFilters.find(filter => variant.basicColor === filter.name)
      });
      if (colorVariants.length > 0) {
        remainingVariants = colorVariants
      }
    }

    /* find by order 1) Likes 2) NEW IN 3) Non-Sale (with Outfit-Image) 4) Non-Sale 5) Rest */

    // find first liked
    for (const variant of remainingVariants) {
      for (let like of state.likes) {
        if (variant.productId === like.productId && variant.id === like.variantId) {
          return variant;
        }
      }
    }

    // find first new in
    for (const variant of remainingVariants) {
      if (variant.newIn) {
        return variant;
      }
    }

    // find first highlight
    for (const variant of remainingVariants) {
      if (variant.highlight) {
        return variant;
      }
    }

    // find first non-sale variant with an outfit image
    for (const variant of remainingVariants) {
      if (!variant.sale) {
        for (const image of variant.images) {
          if (image.type === 'OUTFIT_IMAGE') {
            return variant;
          }
        }
      }
    }

    // find first non-sale and not new in => "normal" variants
    for (const variant of remainingVariants) {
      if (!variant.sale) {
        return variant;
      }
    }

    // if no variant was found until now return the first one
    return remainingVariants[0];
  }

  public hasAdditionalColors() {
    return this.variants.length >= 2;
  }

  public getAdditionalColorsCount() {
    return this.hasAdditionalColors() ? this.variants.length - 1 : 0;
  }

}

export class Description {

  public description: string;
  public language: string;

  constructor(values) {
    this.description = values.description;
    this.language = values.language;
  }
}

export class Color {

  public red: number;
  public green: number;
  public blue: number;

  constructor(red: number, green: number, blue: number) {
    this.red = red;
    this.green = green;
    this.blue = blue
  }

  toCssRGB(): string {
    return `rgb(${this.red}, ${this.green}, ${this.blue})`
  }

}

export class Size {
  public sizeValue: string;
  public sizeName: string;
  public barCode: string;

  constructor(values: any) {
    this.sizeValue = values.size_value;
    this.sizeName = values.size_name;
    this.barCode = values.bar_code;
  }
}

export type ImageType = 'CUTOUT' | 'OUTFIT_VIDEO' | 'OUTFIT_IMAGE'

export class Image {
  public angle: string;
  public key: string;
  public hasThumbnail: boolean;
  public type: ImageType;
  public position: number;

  constructor(values: any) {
    this.angle = values.angle;
    this.key = values.key;
    this.hasThumbnail = values.hasThumbnail ? values.hasThumbnail : values.has_thumbnail ? values.has_thumbnail : false;
    this.type = values.type ? values.type : 'CUTOUT';
    this.position = values.position;
  }
}

export const ImageAngleSort = {};
ImageAngleSort["KEY_VISUAL"] = 1;
ImageAngleSort["FRONT"] = 2;
ImageAngleSort["LEFT"] = 3;
ImageAngleSort["RIGHT"] = 4;
ImageAngleSort["BACK"] = 5;
ImageAngleSort["INSIDE"] = 6;
ImageAngleSort["BOTTOM"] = 7;
ImageAngleSort["TOP"] = 8;
ImageAngleSort["DETAIL"] = 9;

export const ImageTypeSort = {};
ImageTypeSort["OUTFIT_IMAGE"] = 1;
ImageTypeSort["CUTOUT"] = 2;
ImageTypeSort["OUTFIT_VIDEO"] = 3;

export function sortByPositionAndTypeAndAngle(images: Image[]): Image[] {
  if (!images || images.length <= 0) {
    return images;
  }

  images.sort((a, b) => {
    if ('position' in a && 'position' in b && a.position !== b.position && a.type !== 'OUTFIT_VIDEO' && b.type !== 'OUTFIT_VIDEO') {
      return a.position - b.position
    } else {
      // sort all key visual types first, then by angle and than by the rest of the types
      const typeSort1 = ImageTypeSort[a.type];
      const typeSort2 = ImageTypeSort[b.type];
      const angleSort1 = ImageAngleSort[a.angle];
      const angleSort2 = ImageAngleSort[b.angle];
      return typeKeyVisual(a) - typeKeyVisual(b) || typeSort1 - typeSort2 || angleSort1 - angleSort2;
    }
  });

  return images;
}

function typeKeyVisual(image: Image) {
  return image.angle === 'KEY_VISUAL' ? 1 : 2;
}

export class Variant {

  public id: string;
  public productId: string;
  public currency: string;
  public images: Image[];
  public currentPrice: number;
  public originalPrice: number;
  public pantoneColor: string;
  public redPriceChange: boolean;
  public comingSoon: boolean;
  public newIn: boolean;
  public highlight: boolean;
  public sizes: Size[];
  public basicColor: string;
  public colorGroup: string;
  public sale: boolean;
  public red: number;
  public green: number;
  public blue: number;

  private colorName: string;
  private publishDate: Date;


  constructor(values: any) {
    this.basicColor = values.basic_color ? values.basic_color : values.basicColor;
    this.blue = values.blue;
    this.colorGroup = values.color_group ? values.color_group : values.colorGroup;
    this.colorName = values.color_name ? values.color_name : values.colorName;
    this.currency = values.currency;
    this.currentPrice = values.current_price ? values.current_price : values.currentPrice;
    this.green = values.green;
    this.comingSoon = values.coming_soon ? values.coming_soon : values.comingSoon;
    this.newIn = values.new_in ? values.new_in : values.newIn;
    this.highlight = values.highlight ? values.highlight : false;
    this.sale = values.sale ? values.sale : false;

    this.images = [];
    for (let image of values.images) {
      this.images.push(new Image(image))
    }

    this.images = sortByPositionAndTypeAndAngle(this.images);

    this.originalPrice = values.original_price ? values.original_price : values.originalPrice;
    this.pantoneColor = values.pantone_color ? values.pantone_color : values.pantoneColor;
    this.publishDate = values.publish_date ? values.publish_date : values.publishDate;
    this.red = values.red;
    if (this.originalPrice <= this.currentPrice) {
      this.redPriceChange = false
    } else {
      this.redPriceChange = values.red_price_change ? values.red_price_change : values.redPriceChange;
    }

    this.sizes = [];
    for (let size of values.sizes) {
      this.sizes.push(new Size(size))
    }

    this.id = values.id;
    this.productId = values.product_id ? values.product_id : values.productId;
  }

  public hasTag(): boolean {
    return this.comingSoon || this.sale || this.newIn || this.highlight;
  }

  public showPriceLabel(): boolean {
    return !this.comingSoon;
  }

  public getColorTranslation() {
    let key = (this.colorGroup ? `colorgroup_${this.colorGroup}` : `filter_color_${this.basicColor}`).toLowerCase();
    return i18nService.translate(key);
  }
}

export function formatPrice(price: number, currency: string) {
  return price.toLocaleString(App.countryCode, {
    style: "currency",
    currency: currency,
    // @ts-ignore
    currencyDisplay: 'code'
  })
}



export enum Gender {
  None = "NONE",
  Male = "MALE",
  Female = "FEMALE",
}

export function GenderFromString(input: string): Gender {
  if (input) {
    switch (input.toUpperCase()) {
      case Gender.None:
        return Gender.None;
      case Gender.Male:
        return Gender.Male;
    }
  }
  return Gender.Female
}

export enum FilterType {
  Custom = "CUSTOM",
  Brand = "BRAND",
  Category = "CATEGORY",
  Color = "COLOR",
  Like = "LIKE",
  Collection = "COLLECTION",
  Editorial = "EDITORIAL",
}
